import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import { getAccessToken } from '../Utils/auth';

export interface TablePersistenceState {
    columnFilters?: any;
    columnVisibility?: any;
    columnOrder?: any;
    columnSizing?: any;
    columnPinning?: any;
    globalFilter?: any;
    sorting?: any;
    density?: any;
    grouping?: any;
    pagination?: { pageIndex: number; pageSize: number };
    showColumnFilters?: boolean;
    showGlobalFilter?: boolean;
    actionButtonOrder?: string[];
    actionButtonVisibility?: Record<string, boolean>;
    isFullScreen?: boolean;
    // Organization-specific external filter state
    filterType?: "range" | "from" | "to";
    startDate?: string | null;
    endDate?: string | null;
    selectedCommodity?: string[];
    selectedBroker?: string[];
    selectedClearer?: string[];
    selectedBook?: string[];
    filterState?: Record<string, any>;
}

type StateUpdater<T> = T | ((prev: NonNullable<T>) => T);

/**
 * Custom hook to manage persistence of Material React Table (MRT) state.
 * Scopes the data by both the current User ID (from JWT) and a Screen ID.
 * 
 * @param screenId Unique identifier for the screen/table to persist
 * @param initialState Default state to use if no persisted state is found
 * @returns loadState, saveState, clearState, and utility functions
 */
export const useTablePersistence = (screenId: string, initialState: TablePersistenceState = {}) => {
    const userId = useMemo(() => {
        const token = getAccessToken();
        if (!token) return 'anonymous';
        try {
            const decoded: any = jwtDecode(token);
            // Try to find a unique identifier in the token payload
            return decoded.user_id || decoded.id || decoded.sub || 'anonymous';
        } catch (e) {
            console.warn('Failed to decode JWT token for persistence scoping', e);
            return 'anonymous';
        }
    }, []);

    const storageKey = useMemo(() => `mrt_persistence_${userId}_${screenId}`, [userId, screenId]);

    // Internal state to track changes and trigger saves
    const initialStateRef = useRef(initialState);

    const loadSavedState = useCallback(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) as TablePersistenceState : initialStateRef.current;
        } catch (e) {
            console.error('Failed to load table state', e);
            return initialStateRef.current;
        }
    }, [storageKey]);

    const saveStateToStorage = useCallback((nextState: TablePersistenceState) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(nextState));
        } catch (e) {
            console.error('Failed to save table state', e);
        }
    }, [storageKey]);

    const [state, setState] = useState<TablePersistenceState>(loadSavedState);

    const isInitialized = useRef(false);

    // Reload state when the storage key changes (e.g. tab-scoped screens)
    useEffect(() => {
        setState(loadSavedState());
    }, [loadSavedState]);

    // Save state whenever it changes
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            return;
        }

        saveStateToStorage(state);
    }, [state, saveStateToStorage]);

    const resetState = useCallback(() => {
        localStorage.removeItem(storageKey);
        setState(initialStateRef.current);
    }, [storageKey]);

    const updateState = useCallback((updates: Partial<TablePersistenceState>) => {
        setState((prev) => ({ ...prev, ...updates }));
    }, []);

    const setField = <K extends keyof TablePersistenceState>(
        key: K,
        updater: StateUpdater<TablePersistenceState[K]>,
        defaultValue?: NonNullable<TablePersistenceState[K]>
    ) => {
        setState((prev) => {
            const currentValue = prev[key] === undefined ? defaultValue : prev[key];
            return {
                ...prev,
                [key]: typeof updater === 'function'
                    ? (updater as (prevValue: NonNullable<TablePersistenceState[K]>) => TablePersistenceState[K])(
                        currentValue as NonNullable<TablePersistenceState[K]>
                    )
                    : updater
            };
        });
    };

    const setColumnFilters = useCallback(
        (updater: StateUpdater<TablePersistenceState['columnFilters']>) => setField('columnFilters', updater, []),
        []
    );

    const setColumnVisibility = useCallback(
        (updater: StateUpdater<TablePersistenceState['columnVisibility']>) => setField('columnVisibility', updater, {}),
        []
    );

    const setColumnOrder = useCallback(
        (updater: StateUpdater<TablePersistenceState['columnOrder']>) => setField('columnOrder', updater, []),
        []
    );

    const setColumnSizing = useCallback(
        (updater: StateUpdater<TablePersistenceState['columnSizing']>) => setField('columnSizing', updater, {}),
        []
    );

    const setColumnPinning = useCallback(
        (updater: StateUpdater<TablePersistenceState['columnPinning']>) => setField('columnPinning', updater, {}),
        []
    );

    const setGlobalFilter = useCallback(
        (updater: StateUpdater<TablePersistenceState['globalFilter']>) => setField('globalFilter', updater),
        []
    );

    const setSorting = useCallback(
        (updater: StateUpdater<TablePersistenceState['sorting']>) => setField('sorting', updater, []),
        []
    );

    const setDensity = useCallback(
        (updater: StateUpdater<TablePersistenceState['density']>) => setField('density', updater, 'comfortable'),
        []
    );

    const setGrouping = useCallback(
        (updater: StateUpdater<TablePersistenceState['grouping']>) => setField('grouping', updater, []),
        []
    );

    const setPagination = useCallback(
        (updater: StateUpdater<TablePersistenceState['pagination']>) => setField('pagination', updater, { pageIndex: 0, pageSize: 15 }),
        []
    );

    const setShowColumnFilters = useCallback(
        (updater: StateUpdater<TablePersistenceState['showColumnFilters']>) => setField('showColumnFilters', updater, true),
        []
    );

    const setShowGlobalFilter = useCallback(
        (updater: StateUpdater<TablePersistenceState['showGlobalFilter']>) => setField('showGlobalFilter', updater, true),
        []
    );

    const setActionButtonOrder = useCallback(
        (updater: StateUpdater<TablePersistenceState['actionButtonOrder']>) => setField('actionButtonOrder', updater, []),
        []
    );

    const setActionButtonVisibility = useCallback(
        (updater: StateUpdater<TablePersistenceState['actionButtonVisibility']>) => setField('actionButtonVisibility', updater, {}),
        []
    );

    const setIsFullScreen = useCallback(
        (updater: StateUpdater<TablePersistenceState['isFullScreen']>) => setField('isFullScreen', updater, false),
        []
    );

    return {
        state,
        resetState,
        updateState,
        setColumnFilters,
        setColumnVisibility,
        setColumnOrder,
        setColumnSizing,
        setColumnPinning,
        setGlobalFilter,
        setSorting,
        setDensity,
        setGrouping,
        setPagination,
        setShowColumnFilters,
        setShowGlobalFilter,
        setActionButtonOrder,
        setActionButtonVisibility,
        setIsFullScreen,
        storageKey
    };
};
